import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
  useContext,
} from "react";
import {
  Alert,
  Button,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import PageHeader from "../common/PageHeader";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import withRedirect from "../../helpers/hoc/withRedirect";
import {
  markAsPaidDebtToPayAsProperty,
  GetMyBalance,
  fetchDebtsToPayAsPropertyCuentaPending,
  fetchDebtsToPayAsPropertyCuentaPendingTotalPaid,
  fetchDebtsToPayPendingCategories,
  fetchPaymentQuote,
} from "../../helpers/api/finance";
import { checkUserToken } from "../../helpers/api/auth";
import PeriodSelect from "../common/PeriodSelect";
import { useSelector } from "react-redux";
import { Toast } from "./../UI/toast/Toast";
import Loader from "../common/Loader";
import ButtonIcon from "../common/ButtonIcon";
import { faDollarSign, faSearch, faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NumberFormat from "react-number-format";
import { Link } from "react-router-dom";
import SelectBankListItem from "../common/SelectBankListItem";
import { fetchCondominiumBanks } from "../../helpers/api/banks";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { Controller, useForm, useWatch } from "react-hook-form";
import FullPagination from "../common/pagination/FullPagination";
import WizardError from "../auth/register/WizardError";
import SelectCuentasListItem from "../common/SelectCuentasListItem";
import FileUploadArea from "../../components/common/FileUploadArea";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import classNames from "classnames";
import DebtCategoryFilter from "./DebtCategoryFilter";
import RateRuleNotice, { RATE_RULE_CLOSING } from "./RateRuleNotice";
import useSWRFetchPaginationProperty from "../../helpers/hooks/useSWRFetchPaginationProperty";
import { CurrencyContext } from "../../context/Context";
import Datetime from "react-datetime";
import moment from "moment";
import InputWithLeftSelect from "../common/CurrencyAmountInput";
import { findAllCurrencies, findAllExchangeRates } from "../../helpers/api/currency";
import useExchangeRateForDate from "../../helpers/hooks/useExchangeRateForDate";
import CreateExhangeRateModal from "../exchange/components/CreateExchangeRateModal";
import RedeemCoinsPanel from "../rewards/RedeemCoinsPanel";
import { REWARDS_ENABLED } from "../../helpers/rewardsEnabled";
import {
  convertToUsd,
  convertFromUsd,
  formatAmountRounded,
  getCurrencyName,
  roundAmount,
} from "../../helpers/currencyConversion";
import { fetchAllPages } from "../../helpers/api/pagination";
import usePendingDebtPeriods from "../../helpers/hooks/usePendingDebtPeriods";

// Tamaño de página para las deudas que se van a pagar (no para el listado
// visible, que sí se pagina de a 5). Holgado para una propiedad en un período.
const DEBTS_PAGE_SIZE = 500;

const PropertyToPaysDetails = ({ setRedirect, setRedirectUrl }) => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { role, token } = useSelector((state) => state.auth.user);
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  // `selectedCondominium` es el selector global de la barra superior --
  // solo se setea para managers (ver NavbarVertical.jsx). Un propietario
  // entrando a "Mis cuentas por pagar" nunca lo tiene seteado (queda null
  // toda la sesión), así que todo lo que dependía de él acá (períodos,
  // monedas, tasa de cambio) nunca cargaba para un propietario. Para ese
  // rol, se busca el condominio real desde su propio perfil.
  const [myCondominiumId, setMyCondominiumId] = useState(null);
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[PropertyToPaysDetails DEBUG] role:", role, "token:", token);
    if (role !== "property" || !token) return;
    let isMounted = true;
    (async () => {
      try {
        const profile = await checkUserToken(token);
        // eslint-disable-next-line no-console
        console.log("[PropertyToPaysDetails DEBUG] checkUserToken response:", profile);
        // CondominiumResource devuelve la PK como `condominium_id`, no `id`.
        if (isMounted) setMyCondominiumId(profile?.condominium?.condominium_id ?? profile?.condominium?.id ?? profile?.condominium_id ?? null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log("[PropertyToPaysDetails DEBUG] checkUserToken error:", error);
        if (isMounted) handleErrorMessage(error);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [role, token]);
  const effectiveCondominiumId =
    role === "property" ? myCondominiumId : selectedCondominium;
  const {
    periods: availableDebtPeriods,
    isLoading: isLoadingDebtPeriods,
    hasError: debtPeriodsError,
  } = usePendingDebtPeriods(effectiveCondominiumId);
  // eslint-disable-next-line no-console
  console.log("[PropertyToPaysDetails DEBUG] myCondominiumId:", myCondominiumId, "effectiveCondominiumId:", effectiveCondominiumId);
  const [params, setParams] = useState({
    condominiumId: effectiveCondominiumId,
    search: null,
  });
  const { user } = useSelector((state) => state.auth);
  // Un propietario no debe poder fijar el valor de una tasa de cambio (podría
  // declarar una que le convenga); solo manager/root ven "Registrar tasa".
  const canRegisterRate = user?.role !== "property";
  const [search, setSearch] = useState("");
  const [selectedCuenta, setSelectedCuenta] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [bank, setBank] = useState(null);
  const [fund, setFund] = useState([]);
  const [current, setCurrent] = useState([]);
  const [file, setFile] = useState(null);
  const [amount, setAmount] = useState(0);
  const [cash, setCash] = useState([]);
  const [balance, setBalance] = useState({
    data: 0,
    loading: true,
    error: null,
  });
  // HO-TASK-285: un canje de Homelan Coins suma al saldo a favor; subir esta
  // clave vuelve a pedir el saldo para que "Caja" lo vea sin recargar.
  const [balanceRefreshKey, setBalanceRefreshKey] = useState(0);
  const [totals, setTotals] = useState({
    approved: 0,
    pending: 0,
    total_amount: 0
  });
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [hasLoadedCurrencies, setHasLoadedCurrencies] = useState(false);
  const [hasInitializedCurrencyDefault, setHasInitializedCurrencyDefault] = useState(false);
  const lastAutoAmountRef = useRef("");
  const hasManualAmountOverrideRef = useRef(false);

  // Tras un pago exitoso se redirige, lo que desmonta esta pantalla mientras
  // save() todavia tiene que apagar el estado de "enviando". Sin esta guarda,
  // React avisa de una actualizacion sobre un componente desmontado.
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /*   const {
    data,
    handler: { goToPage, nextPage, perPage, previousPage },
    isLoading,
    meta,
  } = useSWRFetchPagination(fetchCondominiumBanks, params);
*/

  const [paramPeriod, setParamPeriod] = useState({
    condominiumId: effectiveCondominiumId,
  });
  const [paramPeriodTotal, setParamPeriodTotal] = useState({
    condominiumId: effectiveCondominiumId,
  });
  const [paramPeriodIncome, setParamPeriodIncome] = useState({
    condominiumId: effectiveCondominiumId,
    income_only: 1,
  });
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [categoryTotals, setCategoryTotals] = useState(null);
  const rowBsById = useMemo(
    () =>
      Object.fromEntries(
        (categoryTotals?.rows || []).map((row) => [String(row.id), row.amount_bs])
      ),
    [categoryTotals]
  );

  // effectiveCondominiumId llega async (perfil del propietario) -- estos
  // useState ya montaron con null, así que hay que sincronizarlo cuando
  // resuelve.
  useEffect(() => {
    if (!effectiveCondominiumId) return;
    setParams((prev) => ({ ...prev, condominiumId: effectiveCondominiumId }));
    setParamPeriod((prev) => ({ ...prev, condominiumId: effectiveCondominiumId }));
    setParamPeriodTotal((prev) => ({ ...prev, condominiumId: effectiveCondominiumId }));
    setParamPeriodIncome((prev) => ({ ...prev, condominiumId: effectiveCondominiumId }));
  }, [effectiveCondominiumId]);

  const {
    data,
    handler: { goToPage, nextPage, perPage, previousPage },
    meta,
  } = useSWRFetchPagination(
    fetchDebtsToPayAsPropertyCuentaPending,
    paramPeriod
  );

  // data2 NO es el listado visible: es lo que realmente se paga y se manda en
  // propertyDebtsToPay[]. Con el perPage por defecto del hook (5), un período
  // con más de 5 deudas se pagaba y conciliaba parcial, sin ningún aviso.
  const { data: data2, meta: metaDebts } = useSWRFetchPaginationProperty(
    fetchDebtsToPayAsPropertyCuentaPending,
    paramPeriod,
    undefined,
    1,
    DEBTS_PAGE_SIZE
  );

  // Filas de ingreso aplicadas a este período: se muestran aparte, como un
  // descuento ya pagado, no como deudas seleccionables para "Emitir pago".
  const { data: dataIncomes, meta: metaIncomes } = useSWRFetchPaginationProperty(
    fetchDebtsToPayAsPropertyCuentaPending,
    paramPeriodIncome,
    undefined,
    1,
    DEBTS_PAGE_SIZE
  );

  const incomeCreditByPeriod = useMemo(() => {
    return dataIncomes.reduce((acc, cuenta) => {
      const periodId = cuenta.period?.id;
      if (!periodId) return acc;
      acc[periodId] = (acc[periodId] || 0) + Math.abs(cuenta.amount);
      return acc;
    }, {});
  }, [dataIncomes]);

  const incomeCreditTotal = useMemo(
    () => Object.values(incomeCreditByPeriod).reduce((a, b) => a + b, 0),
    [incomeCreditByPeriod]
  );

  const {
    data: condominiumBanks,
    handler: {
      goToPage: cbGoToPage,
      nextPage: cbNextPage,
      perPage: cbPerPage,
      previousPage: cbPreviousPage,
    },
    isLoading: cbIsLoading,
    meta: cbMeta,
  } = useSWRFetchPagination(fetchCondominiumBanks, params);

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    setValue,
    control,
    clearErrors,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    shouldUnregister: false,
    defaultValues: {
      montoOriginal: "",
      monedaOriginal: curr?.currency?.id ? String(curr.currency.id) : "",
      payment_date: moment().format("YYYY-MM-DD"),
    },
  });

  const [isPending, setIsPending] = useState(false);

  const watchedOriginalAmount = useWatch({
    control,
    name: "montoOriginal",
  });

  const watchedOriginalCurrency = useWatch({
    control,
    name: "monedaOriginal",
    defaultValue: "",
  });

  const watchedPaymentDate = useWatch({
    control,
    name: "payment_date",
    defaultValue: moment().format("YYYY-MM-DD"),
  });

  const currencyCatalog = useMemo(() => {
    const normalized = (availableCurrencies || []).map((item) => ({
      ...item,
      id: String(item.id),
      iso: (item?.iso || "").toUpperCase(),
      isFallbackUsd: false,
    }));

    const hasUsd = normalized.some((item) => item.iso === baseCurrency.iso);
    if (!hasUsd) {
      normalized.push({
        id: baseCurrency.iso,
        iso: baseCurrency.iso,
        name: getCurrencyName(baseCurrency.iso),
        isFallbackUsd: true,
      });
    }

    return normalized;
  }, [availableCurrencies, baseCurrency.iso]);

  const preferredDefaultCurrency = useMemo(() => {
    const bolivaresCurrency = currencyCatalog.find((item) => {
      const name = (item?.name || "").toLowerCase();
      const iso = (item?.iso || "").toLowerCase();
      const symbol = (item?.symbol || "").toLowerCase();

      return name.includes("boliva") || iso === "ves" || symbol === "bs" || iso === "bs";
    });

    const fallbackByBase = currencyCatalog.find(
      (item) => (item?.iso || "").toUpperCase() === baseCurrency.iso
    );

    return bolivaresCurrency || fallbackByBase || currencyCatalog[0] || null;
  }, [baseCurrency.iso, currencyCatalog]);

  const selectedCurrencyData = useMemo(() => {
    const currentValue = String(watchedOriginalCurrency || "");
    return (currencyCatalog || []).find(
      (item) =>
        String(item.id) === currentValue ||
        item.iso === currentValue.toUpperCase()
    );
  }, [currencyCatalog, watchedOriginalCurrency]);

  const isSelectedCurrencyUsd =
    (selectedCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

  const parsedPaymentDate = useMemo(() => {
    if (!watchedPaymentDate) {
      return null;
    }

    const parsedDate = moment(watchedPaymentDate);
    return parsedDate.isValid() ? parsedDate : null;
  }, [watchedPaymentDate]);

  useEffect(() => {
    let isMounted = true;

    const loadCurrencies = async () => {
      if (!effectiveCondominiumId) return;

      setHasLoadedCurrencies(false);

      try {
        const response = await fetchAllPages((page, pageSize) => findAllCurrencies(page, pageSize, {
          condominium_id: effectiveCondominiumId,
        }));

        if (isMounted) {
          setAvailableCurrencies(response?.data || []);
        }
      } catch (error) {
        if (isMounted) {
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setHasLoadedCurrencies(true);
        }
      }
    };

    loadCurrencies();

    return () => {
      isMounted = false;
    };
  }, [effectiveCondominiumId]);

  useEffect(() => {
    if (!hasLoadedCurrencies || hasInitializedCurrencyDefault || !preferredDefaultCurrency) {
      return;
    }

    setValue("monedaOriginal", String(preferredDefaultCurrency.id), {
      shouldDirty: false,
      shouldValidate: true,
    });

    setHasInitializedCurrencyDefault(true);
  }, [hasLoadedCurrencies, hasInitializedCurrencyDefault, preferredDefaultCurrency, setValue]);

  const activeExchangeRate = Number(curr?.value ?? 0);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);

  const {
    rateByDate,
    rateResolution,
    isRateLoading,
    appliedExchangeRate: hookExchangeRate,
    bumpRateRefreshTick,
  } = useExchangeRateForDate({
    currencyId: selectedCurrencyData?.id,
    isUsdCurrency: isSelectedCurrencyUsd || Boolean(selectedCurrencyData?.isFallbackUsd),
    date: parsedPaymentDate,
    activeExchangeRate,
    condominiumId: effectiveCondominiumId,
  });

  const isCondominiumCurrency =
    Boolean(selectedCurrencyData?.id) &&
    String(selectedCurrencyData?.id) === String(curr?.currency?.id);
  const backendPaymentRate = Number(categoryTotals?.exchange_rate?.value || 0);
  const appliedExchangeRate =
    isCondominiumCurrency && backendPaymentRate > 0 ? backendPaymentRate : hookExchangeRate;
  // La cotización cubre todas las planillas elegidas; el desglose, una sola.
  const rateRule = paymentQuote?.rate_rule ?? categoryTotals?.rate_rule ?? null;
  // Dentro del periodo de gracia la tasa es la del cierre del mes y la pone el
  // backend: que falte la del día del pago no importa, y no se puede cambiar.
  const isRateFrozen =
    isCondominiumCurrency &&
    backendPaymentRate > 0 &&
    categoryTotals?.rate_rule?.type === RATE_RULE_CLOSING;
  const bsOf = (backendBs, base) =>
    isCondominiumCurrency && backendBs !== undefined && backendBs !== null
      ? Number(backendBs)
      : Number(base || 0) * appliedExchangeRate;

  const isSelectedDateToday =
    Boolean(parsedPaymentDate) && parsedPaymentDate.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");

  const groupedAccounts = selectedCuenta.reduce((acc, cuenta) => {
    const periodId = cuenta.period?.id ?? "sin-periodo";
    if (!acc[periodId]) {
      acc[periodId] = [];
    }
    acc[periodId].push(cuenta);
    return acc;
  }, {});

  // % de descuento por pronto pago vigente HOY para el período seleccionado
  // (0 si el período no tiene descuento configurado o ya venció la fecha
  // límite).
  const discountPercentByPeriod = useMemo(() => {
    const list = (data2 && data2.length > 0) ? data2 : (data || []);
    return list.reduce((acc, cuenta) => {
      const period = cuenta.period;
      if (!period?.id || acc[period.id] !== undefined) return acc;
      const pct = Number(period.discount_percentage) || 0;
      const dueDate = period.discount_due_date;
      const paymentMoment = parsedPaymentDate || moment();
      const eligible =
        pct > 0 && !!dueDate && paymentMoment.isSameOrBefore(moment(dueDate), "day");
      acc[period.id] = eligible ? pct : 0;
      return acc;
    }, {});
  }, [data2, data, parsedPaymentDate]);

  // Monto que se ahorraría por pronto pago sobre lo actualmente seleccionado,
  // ya neto del crédito por ingresos/descuentos ya aplicados a cada período.
  const prospectiveDiscountByPeriod = useMemo(() => {
    return Object.entries(groupedAccounts).reduce((acc, [periodId, accounts]) => {
      const pct = discountPercentByPeriod[periodId] || 0;
      if (!pct) {
        acc[periodId] = 0;
        return acc;
      }
      const rawTotal = accounts.reduce((a, c) => a + c.amount, 0);
      const netBeforeDiscount = Math.max(
        rawTotal - (incomeCreditByPeriod[periodId] || 0),
        0
      );
      // Redondeado a centavos (igual que el backend), ver comentario
      // equivalente en PropertyToPays.jsx.
      acc[periodId] = Math.round(netBeforeDiscount * (pct / 100) * 100) / 100;
      return acc;
    }, {});
  }, [groupedAccounts, discountPercentByPeriod, incomeCreditByPeriod]);

  const prospectiveDiscountTotal = useMemo(
    () => Object.values(prospectiveDiscountByPeriod).reduce((a, b) => a + b, 0),
    [prospectiveDiscountByPeriod]
  );

  // Deuda seleccionada menos el descuento ya aplicado por ingresos/pronto
  // pago a planilla de este período, menos el pronto pago prospectivo (si hoy
  // todavía está dentro de la fecha límite del período). Nunca baja de 0.
  const netAmount = Math.max(
    amount - incomeCreditTotal - prospectiveDiscountTotal,
    0
  );

  // Solo se usa como respaldo para varios períodos a la vez, donde el backend
  // no da totales: ver maxPayableUsd, que es la fuente de verdad. No se muestra
  // ni se prellena con esto, porque resta totals.total_amount --lo pagado del
  // período COMPLETO-- sobre un listado que ya trae solo las deudas con
  // paymented = 0, y termina descontando dos veces lo ya pagado.
  const maxPayableAmount = useMemo(() => {
    if (!paramPeriod.period_id) {
      return 0;
    }

    return Math.max(netAmount - (totals.total_amount || 0), 0);
  }, [netAmount, paramPeriod.period_id, totals.total_amount]);

  const usdEquivalent = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return null;
    }

    const quoteMatchesInput =
      paymentQuote &&
      paymentQuote.amount_entered !== null &&
      paymentQuote.amount_entered !== undefined &&
      Number(paymentQuote.amount_entered) === Number(watchedOriginalAmount);
    if (quoteMatchesInput && paymentQuote.amount_base !== null && paymentQuote.amount_base !== undefined) {
      return Number(paymentQuote.amount_base);
    }

    const convertedAmount = convertToUsd(
      watchedOriginalAmount,
      false,
      appliedExchangeRate
    );

    return convertedAmount === "" ? null : convertedAmount;
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount, paymentQuote]);

  const helpText = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return "";
    }

    const effectiveRateDate = parsedPaymentDate?.isValid()
      ? parsedPaymentDate
      : (rateResolution?.usedDateKey ? moment(rateResolution.usedDateKey) : moment());
    const dateLabel = isRateFrozen
      ? `congelada ${moment(categoryTotals.rate_rule.closing_date).format("DD/MM/YYYY")}`
      : effectiveRateDate.format("DD/MM/YYYY");

    if (isRateLoading) {
      return "";
    }

    if (usdEquivalent === null) {
      return "";
    }

    const rateLabel = appliedExchangeRate
      ? formatAmountRounded(appliedExchangeRate, 2)
      : "N/D";

    return `Tasa ${dateLabel}: ${rateLabel} | Equivalente aproximado: ${baseCurrency.symbol}${formatAmountRounded(usdEquivalent, 2)} ${baseCurrency.iso}`;
  }, [
    appliedExchangeRate,
    isRateLoading,
    isSelectedCurrencyUsd,
    parsedPaymentDate,
    rateResolution,
    usdEquivalent,
    baseCurrency,
    isRateFrozen,
    categoryTotals,
  ]);

  const resolveRateForCurrency = async (currencyData) => {
    if (!currencyData) {
      return null;
    }

    const isUsdCurrency =
      (currencyData?.iso || "").toUpperCase() === baseCurrency.iso ||
      currencyData?.isFallbackUsd;

    if (isUsdCurrency) {
      return 1;
    }

    // La moneda del condominio se convierte con la tasa del backend, que es
    // la congelada al cierre cuando el pago cae en el periodo de gracia.
    if (String(currencyData.id) === String(curr?.currency?.id) && backendPaymentRate > 0) {
      return backendPaymentRate;
    }

    try {
      const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
        currency: currencyData.id,
      }));

      const rates = response?.data || [];
      const selectedDateKey = parsedPaymentDate?.format("YYYY-MM-DD");
      const rateForDay = selectedDateKey
        ? rates
          .filter(
            (rate) =>
              moment(rate.date || rate.created_at).format("YYYY-MM-DD") ===
              selectedDateKey
          )
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0]
        : null;

      if (selectedDateKey && !rateForDay) {
        return 0;
      }

      return Number(rateForDay?.value || appliedExchangeRate || Number(curr?.value ?? 0) || 0);
    } catch (error) {
      return Number(appliedExchangeRate || Number(curr?.value ?? 0) || 0);
    }
  };

  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value;
    const nextCurrencyData = (currencyCatalog || []).find(
      (item) => String(item.id) === String(nextCurrency)
    );
    const currentCurrencyData = selectedCurrencyData;

    if (!nextCurrencyData || !currentCurrencyData) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    const currentIsUsd =
      (currentCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;
    const nextIsUsd =
      (nextCurrencyData?.iso || "").toUpperCase() === baseCurrency.iso;

    if (!currentIsUsd && !nextIsUsd) {
      setValue("monedaOriginal", nextCurrency, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    let nextOriginalAmount = watchedOriginalAmount;

    if (currentIsUsd && !nextIsUsd) {
      const targetRate = await resolveRateForCurrency(nextCurrencyData);

      if (!targetRate || targetRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertFromUsd(
        watchedOriginalAmount,
        false,
        targetRate
      );
    } else if (!currentIsUsd && nextIsUsd) {
      const sourceRate =
        Number(appliedExchangeRate || 0) > 0 && !rateResolution?.isFallback
          ? Number(appliedExchangeRate)
          : await resolveRateForCurrency(currentCurrencyData);

      if (!sourceRate || sourceRate <= 0) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa del dia disponible para convertir la moneda seleccionada.",
        });
        return;
      }

      nextOriginalAmount = convertToUsd(
        watchedOriginalAmount,
        false,
        sourceRate
      );
    }

    setValue("monedaOriginal", nextCurrency, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      "montoOriginal",
      nextOriginalAmount === "" ? "" : String(nextOriginalAmount),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
  };

  useEffect(() => {
    if (selectedCuenta) {
      clearErrors("propertyDebtsToPay");
    }
  }, [selectedCuenta]);

  useEffect(() => {
    if (selectedBankId) {
      clearErrors("bank_id");
    }
  }, [selectedBankId]);

  useEffect(() => {
    let isMounted = true;
    if (user) {
      const getBalance = async () => {
        try {
          const {
            data: { balance },
          } = await GetMyBalance();
          isMounted &&
            setBalance((prev) => {
              return { ...prev, data: balance, loading: false };
            });
        } catch (error) {
          isMounted &&
            setBalance((prev) => {
              return { ...prev, error, loading: false };
            });
        }
      };
      getBalance();
    }
    return () => {
      isMounted = false;
    };
  }, [user, balanceRefreshKey]);

  // HO-TASK-285: mismo efecto que elegir "Caja" en el selector de origen de
  // fondos, para pagar con el saldo a favor que dejó un canje.
  const selectBalanceAsFunds = () => {
    setFund(false);
    setCurrent(true);
    setCash(false);
    setValue("payment_by_account", "1");
    clearErrors("payment_by_account");
  };

  const handleImageChange = (event) => {
    if (event.target.files.length !== 0) {
      setImagePreview(URL.createObjectURL(event.target.files[0]));
      register("voucher");
      setValue("voucher", event.target.files[0]);
      setFile(event.target.files[0]);
    }
  };

  const save = async (values) => {

    const { payment_date, montoOriginal, monedaOriginal } = values;
    let quote = null;
    try {
      quote = await fetchPaymentQuote({
        ...quoteParams,
        period_ids: quotePeriodIds,
        currency_id: monedaOriginal || quoteParams.currency_id,
        payment_date: payment_date || quoteParams.payment_date,
        amount: montoOriginal,
      });
    } catch (error) {
      quote = null;
    }
    const typedUsdValue =
      quote && quote.amount_base !== null && quote.amount_base !== undefined
        ? Number(quote.amount_base)
        : convertToUsd(
            montoOriginal,
            isSelectedCurrencyUsd,
            appliedExchangeRate
          );
    const normalizedUsdValue =
      !hasManualAmountOverrideRef.current && paramPeriod.period_id && typedUsdValue !== ""
        ? maxPayableUsd
        : typedUsdValue;

    // Monto en la moneda del banco. Esta vista nunca lo mandaba, asi que al
    // aprobar la planilla el backend se quedaba sin el dato y
    // changeMovementsProperty cortaba con "amount_converted is required when
    // the selected bank has currency": los pagos reportados contra un banco en
    // otra moneda no se podian aprobar.
    //
    // Si se pago en la moneda del banco es el monto tal cual; si se pago en
    // dolares se convierte con la tasa del condominio (curr), no con
    // appliedExchangeRate: esa ultima es la tasa de la moneda ELEGIDA para
    // pagar, y vale 1 cuando se paga en dolares — con ella el convertido salia
    // igual al monto en dolares en vez de en bolivares. Misma formula que la
    // vista de admin.
    const contextCurrencyIso = (curr?.currency?.iso || "").toUpperCase();
    const contextIsBaseCurrency = contextCurrencyIso === baseCurrency.iso;
    const convertedToContextCurrency = convertFromUsd(
      montoOriginal,
      contextIsBaseCurrency,
      Number(curr?.value ?? 0)
    );
    const amountConverted =
      quote && quote.amount_converted !== null && quote.amount_converted !== undefined
        ? Number(quote.amount_converted)
        : roundAmount(
      !isSelectedCurrencyUsd
        ? montoOriginal
        : convertedToContextCurrency === ""
          ? montoOriginal
          : convertedToContextCurrency
    );
    const quotedTotal = quote?.periods?.[0]?.total_amount;
    const billTotalToSend =
      quotedTotal !== undefined && quotedTotal !== null ? Number(quotedTotal) : billTotalUsd;
    const rateToStore = isSelectedCurrencyUsd
      ? Number(quote?.rate_rule?.rate || backendPaymentRate || activeExchangeRate || appliedExchangeRate || 0)
      : appliedExchangeRate;

    if (!payment_date) {
      Toast.fire({
        type: "error",
        title: "La fecha del pago es obligatoria.",
      });
      return;
    }

    // Solo se paga lo que esta en data2/dataIncomes: si el backend reporta mas
    // filas de las que trajimos, el monto y los ids estarian incompletos y se
    // pagaria de menos en silencio. Preferimos no reportar el pago.
    const debtsMissing =
      metaDebts.total !== null && data2.length < metaDebts.total;
    const incomesMissing =
      metaIncomes.total !== null && dataIncomes.length < metaIncomes.total;

    if (debtsMissing || incomesMissing) {
      Toast.fire({
        type: "error",
        title:
          "Este periodo tiene mas cuentas de las que se pudieron cargar. Recarga la pagina o selecciona menos periodos antes de reportar el pago.",
      });
      return;
    }

    // Reportando "Todo" se admite pagar de mas: al aprobar la planilla, el
    // excedente queda como saldo a favor de la propiedad. Con una categoría
    // elegida no, porque el abono es del período completo y no de una
    // categoría — el excedente quedaría sin poder decir contra qué se aplicó.
    //
    // Misma tolerancia de medio centavo que la vista de admin, para no
    // rechazar por redondeos de conversión.
    if (
      !allowsOverpayment &&
      Number(normalizedUsdValue) > maxPayableUsd + 0.005
    ) {
      Toast.fire({
        type: "error",
        title: "El monto ingresado supera el monto maximo permitido.",
      });
      return;
    }

    if (
      !isSelectedCurrencyUsd &&
      (!appliedExchangeRate || appliedExchangeRate <= 0 || (rateResolution?.isFallback && !isRateFrozen))
    ) {
      Toast.fire({
        type: "error",
        title: "No hay una tasa del dia disponible para convertir esta moneda a USD.",
      });
      return;
    }

    if (normalizedUsdValue === "" || Number(normalizedUsdValue) <= 0) {
      Toast.fire({
        type: "error",
        title: "Ingresa un monto valido para procesar el pago.",
      });
      return;
    }

    // Identifica este intento de pago. Si la peticion se reenvia —reintento del
    // cliente, timeout del upload del comprobante, dos pestañas— el backend
    // reconoce la clave y devuelve la planilla que ya creo, en vez de reportar
    // el pago dos veces.
    const attemptId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    setIsPending(true);
    // Al elegir "Caja" cash queda en false, asi que este `cash === false` se
    // comia la rama de Caja de mas abajo y el pago salia con
    // payment_by_account = 0, o sea como una transferencia sin banco: la opcion
    // estaba en el selector pero nunca gastaba el saldo del propietario. Se
    // excluye current para que la rama que manda el 1 sea alcanzable.
    if (cash === false && current !== true) {
      const { bank_id, reference, payment_by_account, note } = values;
      try {
        const formData = new FormData();

        formData.append("idempotency_key", attemptId);
        formData.append("amount", Number(normalizedUsdValue));
        formData.append("amount_converted", amountConverted);
        formData.append("total_amount", billTotalToSend);
        if (paramPeriod.period_id) {
          formData.append("period_id", paramPeriod.period_id);
        }
        formData.append("payment_by_account", 0);
        formData.append("payment_date", payment_date);
        formData.append("montoOriginal", montoOriginal);
        formData.append("monedaOriginal", monedaOriginal);
        formData.append("exchange_rate", appliedExchangeRate || 1);
        if (rateToStore) formData.append("rate", rateToStore);
        if (paramPeriod.period_id) {
          formData.append("period_id", paramPeriod.period_id);
        }
        if (payment_by_account !== 1) {
          formData.append("bank_id", bank_id);
          formData.append("reference", reference);
          if (file) formData.append("voucher", file);
        }
        if (note) {
          formData.append("note", note);
        }
        selectedCuenta.map((c, i) =>
          formData.append(`propertyDebtsToPay[${i}]`, c.id)
        );

        const { code } = await markAsPaidDebtToPayAsProperty(formData);
        if (code === SUCCESS_RESPONSE.CREATED) {
          collapseOneOpen(false);
          setRedirectUrl(`/dashboard/my/finance/topay`);
          setRedirect(true);
          Toast.fire({
            type: "success",
            title: `Pago emitido exitosamente, espere su aprobación.`,
          });
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    } else if (current === true) {
      const { note } = values;
      try {
        const formData = new FormData();

        formData.append("idempotency_key", attemptId);
        formData.append("amount", Number(normalizedUsdValue));
        formData.append("amount_converted", amountConverted);
        formData.append("total_amount", billTotalToSend);
        if (paramPeriod.period_id) {
          formData.append("period_id", paramPeriod.period_id);
        }
        formData.append("payment_by_account", 1);
        formData.append("payment_date", payment_date);
        formData.append("montoOriginal", montoOriginal);
        formData.append("monedaOriginal", monedaOriginal);
        formData.append("exchange_rate", appliedExchangeRate || 1);
        if (rateToStore) formData.append("rate", rateToStore);
        /*    if (payment_by_account !== 1) {
          formData.append("bank_id", bank_id);
          formData.append("reference", reference);
        } */
        if (note) {
          formData.append("note", note);
        }
        selectedCuenta.map((c, i) =>
          formData.append(`propertyDebtsToPay[${i}]`, c.id)
        );

        const { code } = await markAsPaidDebtToPayAsProperty(formData);
        if (code === SUCCESS_RESPONSE.CREATED) {
          collapseOneOpen(false);
          setRedirectUrl(`/dashboard/my/finance/topay`);
          setRedirect(true);
          Toast.fire({
            type: "success",
            title: `Pago emitido exitosamente, espere su aprobación.`,
          });
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    }

    if (cash === true) {
      const { payment_by_account, note } = values;
      try {
        const formData = new FormData();

        formData.append("idempotency_key", attemptId);
        formData.append("amount", Number(normalizedUsdValue));
        formData.append("amount_converted", amountConverted);
        formData.append("total_amount", billTotalToSend);
        if (paramPeriod.period_id) {
          formData.append("period_id", paramPeriod.period_id);
        }
        formData.append("payment_by_account", 0);
        formData.append("payment_date", payment_date);
        formData.append("montoOriginal", montoOriginal);
        formData.append("monedaOriginal", monedaOriginal);
        formData.append("exchange_rate", appliedExchangeRate || 1);
        if (rateToStore) formData.append("rate", rateToStore);
        if (payment_by_account !== 1) {
          if (file) formData.append("voucher", file);
        }
        if (note) {
          formData.append("note", note);
        }
        selectedCuenta.map((c, i) =>
          formData.append(`propertyDebtsToPay[${i}]`, c.id)
        );

        const { code } = await markAsPaidDebtToPayAsProperty(formData);
        if (code === SUCCESS_RESPONSE.CREATED) {
          collapseOneOpen(false);
          setRedirectUrl(`/dashboard/my/finance/topay`);
          setRedirect(true);
          Toast.fire({
            type: "success",
            title: `Pago emitido exitosamente, espere su aprobación.`,
          });
        }
      } catch (error) {
        handleErrorMessage(error);
      }
    }
    if (isMountedRef.current) {
      setIsPending(false);
    }
  };

  const handleSelectAll = () => {
    setSelectedCuenta(data2);
  };

  const renderBanksItems = () => {
    return condominiumBanks.map((bank, i) => {
      return (
        <SelectBankListItem
          key={i}
          id={bank.condominium_banks_id}
          selected={selectedBankId}
          onSelect={() => {
            setBank(bank);
            setSelectedBankId(bank.condominium_banks_id);
            setValue("bank_id", bank.condominium_banks_id);
          }}
          title={bank?.name}
          alternativeName="banco"
          createdAt={bank.created_at}
          owner_dni={bank.owner_dni}
          owner_name={bank.owner_name}
          account={bank.account_number}
        />
      );
    });
  };

  const renderCuentasItems = () => {
    return data.map((cuenta, i) => {
      return (
        <SelectCuentasListItem
          key={i}
          id={cuenta.id}
          title={
            cuenta?.name != null && cuenta?.finance?.name
              ? cuenta?.name
                ? cuenta?.name
                : cuenta.finance.name
              : cuenta?.reserve_fund?.name
          }
          alternativeName="banco"
          createdAt={cuenta?.id}
          owner_dni={cuenta?.id}
          owner_name={
            cuenta?.name != null && cuenta?.finance?.name
              ? cuenta?.name
                ? cuenta?.name
                : cuenta.finance.name
              : cuenta?.reserve_fund?.name
          }
          account={cuenta?.amount}
          rate={appliedExchangeRate}
          convertedValue={rowBsById[String(cuenta?.id)]}
          currencyIso={curr?.currency?.iso}
          categoryLabel={getCategoryLabel(cuenta?.category)}
          isPending={cuenta?.is_pending}
        />
      );
    });
  };

  useEffect(() => {
    const value = selectedCuenta.reduce((a, c) => {
      return a + c.amount;
    }, 0);
    setAmount(value);
    handleSelectAll();
  }, [selectedCuenta, data2]);
  const [periodos, setPeriodos] = useState([]);

  useEffect(() => {
    const availableIds = new Set(
      availableDebtPeriods.map((period) => String(period.id))
    );
    setPeriodos((previous) => ({
      ...previous,
      period_id: (previous.period_id || []).filter((periodId) =>
        availableIds.has(String(periodId))
      ),
    }));
  }, [availableDebtPeriods]);

  const fetchApiTotalPaid = useCallback(async () => {
    if (!paramPeriodTotal.period_id) return;
    try {
      const response = await fetchDebtsToPayAsPropertyCuentaPendingTotalPaid(
        paramPeriodTotal
      );
      if (response && typeof response.total_amount === "number") {
        setTotals(response);
      } else {
        console.error("Response is not valid:", response);
        setTotals({ approved: 0, pending: 0, total_amount: 0 });
      }
    } catch (error) {
      console.error(error);
      setTotals({ approved: 0, pending: 0, total_amount: 0 });
    }
  }, [paramPeriodTotal]);

  useEffect(() => {
    fetchApiTotalPaid();
  }, [fetchApiTotalPaid]);

  const paymentDateKey =
    parsedPaymentDate && parsedPaymentDate.isValid()
      ? parsedPaymentDate.format("YYYY-MM-DD")
      : null;
  const fetchCategoryTotals = useCallback(async () => {
    if (!paramPeriod.period_id || !effectiveCondominiumId) return;
    try {
      const response = await fetchDebtsToPayPendingCategories({
        condominiumId: effectiveCondominiumId,
        property_id: null,
        period_id: paramPeriod.period_id,
        payment_date: paymentDateKey || undefined,
      });
      // Siempre se asigna, tambien cuando viene vacio: con un if el estado
      // se quedaba con los totales del periodo anterior, y al pasar de varios
      // periodos a uno los chips no volvian a aparecer.
      setCategoryTotals(response || null);
    } catch (error) {
      console.error("Error fetching category totals:", error);
      setCategoryTotals(null);
    }
  }, [paramPeriod.period_id, effectiveCondominiumId, paymentDateKey]);

  useEffect(() => {
    fetchCategoryTotals();
  }, [fetchCategoryTotals]);

  const quotePeriodIds = useMemo(() => {
    const ids = Array.isArray(paramPeriod.period_id)
      ? paramPeriod.period_id
      : paramPeriod.period_id
      ? [paramPeriod.period_id]
      : [];
    return ids.filter((periodId) => periodId && periodId !== "sin-periodo");
  }, [paramPeriod.period_id]);
  const quoteCurrencyId = selectedCurrencyData?.id ?? null;
  const quoteParams = useMemo(
    () => ({
      condominium_id: effectiveCondominiumId,
      period_ids: quotePeriodIds,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      payment_date: paymentDateKey || undefined,
      currency_id: quoteCurrencyId || undefined,
    }),
    [effectiveCondominiumId, quotePeriodIds, selectedCategory, paymentDateKey, quoteCurrencyId]
  );

  useEffect(() => {
    if (!effectiveCondominiumId || quotePeriodIds.length === 0) {
      setPaymentQuote(null);
      return undefined;
    }
    let isMounted = true;
    const handle = setTimeout(() => {
      fetchPaymentQuote({
        ...quoteParams,
        amount:
          watchedOriginalAmount !== undefined &&
          watchedOriginalAmount !== null &&
          watchedOriginalAmount !== ""
            ? watchedOriginalAmount
            : undefined,
      })
        .then((quote) => {
          if (isMounted) setPaymentQuote(quote || null);
        })
        .catch(() => {
          if (isMounted) setPaymentQuote(null);
        });
    }, 350);
    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [effectiveCondominiumId, quotePeriodIds, quoteParams, watchedOriginalAmount]);

  const handleClick = () => {
    const newPeriodId = periodos.period_id;
    hasManualAmountOverrideRef.current = false;
    // El chip vuelve a "Todo", así que el filtro del servidor también: sin
    // limpiar category el listado seguía filtrado por la categoría del período
    // anterior mientras los chips decían "Todo".
    setParamPeriod((prev) => ({
      ...prev,
      period_id: newPeriodId,
      category: undefined,
    }));
    updateState(setParamPeriodTotal, newPeriodId);
    updateState(setParamPeriodIncome, newPeriodId);
    setSelectedCategory("all");
    goToPage(1);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setParamPeriod((prev) => ({
      ...prev,
      category: category === "all" ? undefined : category,
    }));
    // El listado filtrado casi siempre tiene menos páginas: quedarse en la
    // página en la que estabas mostraba una lista vacía.
    goToPage(1);
  };

  // Montos por categoría desde el backend
  const selectedCategoryData = useMemo(() => {
    if (!categoryTotals || selectedCategory === "all") {
      return null;
    }
    return categoryTotals.categories?.find(
      (c) => c.category === selectedCategory
    ) || null;
  }, [categoryTotals, selectedCategory]);

  // Mapeo de labels para categorías
  const getCategoryLabel = (category) => {
    if (!category) return "Comunes";
    if (category === "reserve_fund") return "Fondos de Reserva";
    if (category === "particular") return "Gastos Particulares";
    if (category === "initial_debt") return "Deuda Inicial";
    return "Comunes";
  };

  // Hay totales por categoría utilizables.
  const hasCategoryTotals = Boolean(
    selectedCategoryData && categoryTotals?.period
  );

  // Se admite reportar de mas siempre que NO se este pagando una categoría
  // puntual: al aprobar la planilla el excedente queda como saldo a favor de
  // la propiedad.
  //
  // Se mira hasCategoryTotals y no selectedCategory === "all" porque con
  // varios períodos los chips no se muestran: ahí no hay categoría en juego y
  // el excedente tiene que funcionar igual, sin depender de un estado que el
  // usuario no ve.
  const allowsOverpayment = !hasCategoryTotals;

  // Deuda del período que ya tiene un pago reportado esperando aprobación. Es
  // plata que el propietario ya mandó: si no se descuenta del tope, la reporta
  // dos veces.
  const pendingApprovalTotal = useMemo(() => {
    if (!categoryTotals?.categories) return 0;
    return categoryTotals.categories.reduce(
      (acc, c) => acc + (c.pending_approval || 0),
      0
    );
  }, [categoryTotals]);

  // Los totales del backend son de UN período (fetchCategoryTotals pide el
  // primero). Con varios seleccionados no sirven para el total del período.
  const hasPeriodTotals = Boolean(
    categoryTotals?.period &&
      (!Array.isArray(paramPeriod.period_id) ||
        paramPeriod.period_id.length <= 1)
  );

  // Monto neto a mostrar: del backend si hay categoría específica, si no del período completo
  const displayNetAmount = useMemo(() => {
    if (hasCategoryTotals) {
      return Math.max(selectedCategoryData.net - selectedCategoryData.paid, 0);
    }
    return netAmount;
  }, [hasCategoryTotals, selectedCategoryData, netAmount]);


  // Descuento pronto pago: aplicado si la categoría cubre el resto, oportunidad si no
  // Porcentaje de pronto pago vigente para el período que se está pagando.
  //
  // already_discounted evita descontar dos veces: si el período ya tiene su
  // fila is_discount, el backend NO vuelve a aplicarlo y exige el neto
  // completo. Y la fecha límite se valida con el mismo criterio del backend:
  // sin ese chequeo la pantalla descontaba sobre un pronto pago vencido, se
  // reportaba de menos y la deuda no saldaba nunca.
  const activeDiscountPercent = useMemo(() => {
    const period = categoryTotals?.period;
    if (!period || period.already_discounted) {
      return 0;
    }

    const pct = Number(period.discount_percentage) || 0;
    if (!pct) return 0;

    const dueDate = period.discount_due_date;
    if (!dueDate) return 0;

    const paymentMoment = parsedPaymentDate || moment();

    return paymentMoment.isSameOrBefore(moment(dueDate), "day") ? pct : 0;
  }, [categoryTotals?.period, parsedPaymentDate]);

  const displayProspectiveDiscount = useMemo(() => {
    // Sin totales del backend no hay con que calcularlo: se usa el estimado
    // del cliente.
    if (!categoryTotals?.period) {
      return prospectiveDiscountTotal;
    }

    if (categoryTotals.period.discount_now !== undefined) {
      return selectedCategoryData
        ? Number(selectedCategoryData.discount_now || 0)
        : Number(categoryTotals.period.discount_now || 0);
    }

    const pct = activeDiscountPercent;

    if (!pct) return 0;

    // Pagando todo el período, el descuento es el del período completo. Sale
    // de la misma cuenta que el monto, para que el desglose no pueda diferir
    // del total.
    if (!selectedCategoryData) {
      return Math.round(categoryTotals.period.net * (pct / 100) * 100) / 100;
    }

    // ¿Cubre esta categoría el resto del período?
    const remainingInPeriod = categoryTotals.period.pending;
    const thisGrossPending = selectedCategoryData.pending;

    if (thisGrossPending >= remainingInPeriod - 0.01) {
      // Sí cubre → aplicar el descuento
      return Math.round(categoryTotals.period.net * (pct / 100) * 100) / 100;
    }

    return 0;
  }, [selectedCategoryData, categoryTotals, activeDiscountPercent, prospectiveDiscountTotal]);

  // Monto a pagar, en USD. Única fuente de verdad: de acá salen la tarjeta, el
  // prellenado del input y la validación de save(), para que no puedan diferir.
  //
  // Con categoría NO se resta totals.total_amount: displayNetAmount ya viene
  // del backend con cat.paid descontado, y total_amount es lo pagado del
  // período COMPLETO. Restarlo encima contaba dos veces lo ya pagado y hacía
  // que, tras pagar una categoría, la siguiente diera 0 y no se pudiera pagar.
  // Lo que sí se descuenta es lo que ya tiene un pago reportado esperando
  // aprobación, para no reportar dos veces la misma plata.
  const maxPayableUsd = useMemo(() => {
    if (hasCategoryTotals) {
      if (selectedCategoryData.payable !== undefined && selectedCategoryData.payable !== null) {
        return Number(selectedCategoryData.payable);
      }
      return Math.max(
        displayNetAmount -
          displayProspectiveDiscount -
          (selectedCategoryData.pending_approval || 0),
        0
      );
    }

    // "Todo" sobre un solo período: se toma del endpoint de categorías, que
    // suma TODAS las filas del período. Derivarlo del listado visible daba de
    // menos, porque ese listado solo trae las deudas con paymented = 0: al
    // pagar una categoría sus deudas desaparecían del bruto pero su plata
    // seguía contándose en totals.total_amount, restándose dos veces.
    if (hasPeriodTotals) {
      const period = categoryTotals.period;
      if (period.payable !== undefined && period.payable !== null) {
        return Number(period.payable);
      }

      return Math.max(
        period.net -
          displayProspectiveDiscount -
          period.paid -
          pendingApprovalTotal,
        0
      );
    }

    // Varios períodos: no hay totales del backend para todos.
    return maxPayableAmount;
  }, [
    hasCategoryTotals,
    hasPeriodTotals,
    categoryTotals,
    pendingApprovalTotal,
    maxPayableAmount,
    displayNetAmount,
    displayProspectiveDiscount,
    selectedCategoryData,
  ]);

  // maxPayableUsd convertido a la moneda elegida: es lo que ve el propietario
  // en la tarjeta y lo que se le prellena en el input.
  //
  // Antes la tarjeta mostraba una cuenta distinta de la que validaba save():
  // con una categoría elegida usaba la buena, pero en la pestaña "Todo" --que
  // es la que se usa siempre-- caía a la vieja, que resta totals.total_amount
  // sobre un listado que ya viene sin las deudas pagadas y descuenta dos veces
  // lo ya pagado. El propietario veía de menos (ej. €24.06 en vez de €31.07) y
  // pagaba de menos, mientras el admin, sobre la misma deuda, mostraba bien.
  // Es el mismo arreglo que ya tenía PropertyToPays, la pantalla del admin.
  const displayGross = hasPeriodTotals
    ? Number(categoryTotals.period.gross || 0)
    : Number(roundAmount(amount) || 0);
  const displayCredit = hasPeriodTotals
    ? Number(categoryTotals.period.income_credit || 0)
    : Number(roundAmount(incomeCreditTotal) || 0);

  const displayMaxPayable = useMemo(() => {
    if (isSelectedCurrencyUsd) {
      return maxPayableUsd;
    }

    const backendBs = hasCategoryTotals
      ? selectedCategoryData?.payable_bs
      : hasPeriodTotals
      ? categoryTotals?.period?.payable_bs
      : undefined;
    if (isCondominiumCurrency && backendBs !== undefined && backendBs !== null) {
      return Number(backendBs);
    }
    return convertFromUsd(maxPayableUsd, false, appliedExchangeRate);
  }, [appliedExchangeRate, isSelectedCurrencyUsd, maxPayableUsd]);

  // Total NETO que cubre la planilla, sin restarle lo ya pagado: es "de cuánto
  // es la deuda", no "cuánto falta". Así, si se paga en dos tandas, ambas
  // planillas declaran el mismo total y se distinguen por su `amount`.
  const billTotalUsd = useMemo(() => {
    if (!hasCategoryTotals) {
      return maxPayableUsd;
    }
    return Math.max(selectedCategoryData.net - displayProspectiveDiscount, 0);
  }, [
    hasCategoryTotals,
    maxPayableUsd,
    selectedCategoryData,
    displayProspectiveDiscount,
  ]);

  const updateState = (setStateFunction, newPeriodId) => {
    setStateFunction((prev) => ({
      ...prev,
      period_id: newPeriodId,
    }));
  };

  const [value, setValues] = useState(0);

  useEffect(() => {
    const convertedValue = convertToUsd(
      watchedOriginalAmount,
      isSelectedCurrencyUsd,
      appliedExchangeRate
    );

    setValues(convertedValue === "" ? 0 : Number(convertedValue));
  }, [appliedExchangeRate, isSelectedCurrencyUsd, watchedOriginalAmount]);

  // `value` está en USD (más abajo se muestra con convertFromUsd), por eso acá
  // va maxPayableUsd y no su versión convertida: la rama de categoría metía el
  // monto ya convertido a la moneda elegida en un estado que el resto del
  // componente lee como dólares.
  useEffect(() => {
    if (!watchedOriginalAmount) {
      setValues(paramPeriod.period_id ? maxPayableUsd : 0);
    }
  }, [paramPeriod.period_id, watchedOriginalAmount, maxPayableUsd]);

  useEffect(() => {
    // El input está en la moneda elegida, así que acá va la versión convertida.
    const amountToUse = roundAmount(displayMaxPayable);
    const normalizedAmount =
      amountToUse === null || amountToUse === undefined
        ? "0"
        : String(amountToUse);

    const currentAmount = String(watchedOriginalAmount ?? "");
    const lastAutoAmount = String(lastAutoAmountRef.current ?? "");

    if (!hasManualAmountOverrideRef.current || Number(currentAmount) === Number(lastAutoAmount) || currentAmount === "") {
      setValue("montoOriginal", normalizedAmount, {
        shouldDirty: false,
        shouldValidate: true,
      });
      lastAutoAmountRef.current = normalizedAmount;
      hasManualAmountOverrideRef.current = false;
    }
  }, [dirtyFields, displayMaxPayable, setValue, watchedOriginalAmount]);

  return (
    <Fragment>
      <PageHeader
        title="Cuentas por pagar"
        titleTag="h2"
        className="mb-3"
        col={{ lg: 9 }}
      />
      {totals.pending > 0 && (
        <Alert color="warning" className="mb-3">
          Tienes pagos por un total de <strong>
            <NumberFormat
              value={totals.pending}
              displayType="text"
              thousandSeparator
              prefix={`${baseCurrency.symbol} `}
              decimalScale={2}
              fixedDecimalScale
            />
          </strong> pendientes de aprobación por el administrador.
        </Alert>
      )}

      <Row form>
        <Col className="pr-lg-2 mb-3" lg={8}>
          <CardBody className="bg-light">
            <Row>
              <Col tag={FormGroup}>
                <Row className="mb-2">
                  <Col xs={12}>
                    <FormGroup className="mb-0">
                      <Label for="acronym">Selecciona un período</Label>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 mr-2" style={{ minWidth: 0 }}>
                          <PeriodSelect
                            isMulti
                            condominiumId={effectiveCondominiumId}
                            availablePeriods={availableDebtPeriods}
                            isLoading={isLoadingDebtPeriods}
                            value={periodos.period_id}
                            onChange={(newValue) =>
                              setPeriodos((prev) => ({
                                ...prev,
                                period_id: newValue,
                              }))
                            }
                            error={!paramPeriod.period_id && !!errors.payment_by_account?.message}
                            placeholder="Seleccione una opción"
                          />
                        </div>
                        <ButtonIcon
                          onClick={handleClick}
                          className="text-nowrap flex-shrink-0"
                          disabled={false}
                          icon={faCheck}
                          color="primary"
                        >
                          Seleccionar
                        </ButtonIcon>
                      </div>
                    </FormGroup>
                  </Col>
                  {debtPeriodsError && (
                    <Col xs={12}>
                      <Alert color="danger" className="mb-0">
                        No se pudieron cargar los períodos con deuda.
                      </Alert>
                    </Col>
                  )}
                  <Col xs={12}>
                    {!paramPeriod.period_id &&
                      errors.payment_by_account?.message && (
                        <span
                          className={classNames(
                            "text-danger d-inline-block fs--1 font-weight-normal d-block"
                          )}
                        >
                          Debes seleccionar al menos una cuenta
                        </span>
                      )}
                  </Col>
                </Row>

                {paramPeriod.period_id && (
                  <Row>
                    <Col>
                      {/* Los chips salen del endpoint de categorías, no de
                          data2: ese listado ya viene filtrado por la categoría
                          elegida, así que al entrar en una desaparecían las
                          demás y no se podía saltar entre ellas sin volver a
                          "Todo".

                          Y solo con un período: los totales (y el prorrateo del
                          crédito) se piden para un único period_id. Ojo: esto
                          envuelve SOLO los chips — el listado de cuentas queda
                          afuera, o desaparecería con varios períodos. */}
                      {hasPeriodTotals && (
                        <DebtCategoryFilter
                          categories={(categoryTotals?.categories || []).map((c) => ({
                            category: c.category,
                            label: getCategoryLabel(c.category),
                            pending: c.pending,
                            paid: c.paid,
                          }))}
                          value={selectedCategory}
                          onChange={handleCategoryChange}
                          currency={baseCurrency.iso}
                        />
                      )}
                      {data.length >= 1 ? (
                        renderCuentasItems()
                      ) : (
                        <p>No se han encontrado cuentas por pagar.</p>
                      )}
                      <FullPagination
                        nextPage={nextPage}
                        prevPage={previousPage}
                        goToPage={goToPage}
                        meta={meta}
                        className={"shadow-none"}
                        posText={"Cuentas"}
                        perPage={perPage}
                      />
                      {dataIncomes && dataIncomes.length > 0 && (
                        <div className="mt-3 mb-3">
                          <h6 className="text-700 font-weight-bold fs--1 mb-2">
                            Ingresos aplicados a esta planilla (descuentos)
                          </h6>
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered bg-white mb-0">
                              <thead className="bg-200">
                                <tr>
                                  <th>Descripción</th>
                                  <th className="text-right">Descuento</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dataIncomes.map((inc) => (
                                  <tr key={inc.id}>
                                    <td>{inc.name || inc.description || "Ingreso a planilla"}</td>
                                    <td className="text-right text-success font-weight-bold">
                                      - {baseCurrency.symbol} {Number(inc.amount || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                                <tr className="bg-light font-weight-bold">
                                  <td>Total ingresos descontados</td>
                                  <td className="text-right text-success">
                                    - {baseCurrency.symbol} {Number(incomeCreditTotal).toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      <div className="card bg-white border mt-3 mb-3 p-3 shadow-none">
                        {selectedCategoryData && categoryTotals ? (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-1 fs--1">
                              <span className="text-700">Subtotal ({getCategoryLabel(selectedCategoryData.category)}):</span>
                              <span className="font-weight-semi-bold">
                                {baseCurrency.symbol} {Number(selectedCategoryData.gross).toFixed(2)} {baseCurrency.iso}
                                {appliedExchangeRate > 0 && ` | Bs ${bsOf(selectedCategoryData.gross_bs, selectedCategoryData.gross).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                              </span>
                            </div>
                            {selectedCategoryData.credit_share > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 fs--1 text-success font-weight-bold">
                                <span>Crédito por ingreso (prorrateado):</span>
                                <span>
                                  - {baseCurrency.symbol} {Number(selectedCategoryData.credit_share).toFixed(2)} {baseCurrency.iso}
                                  {appliedExchangeRate > 0 && ` | - Bs ${bsOf(selectedCategoryData.credit_share_bs, selectedCategoryData.credit_share).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            )}
                            {displayProspectiveDiscount > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 fs--1 text-success font-weight-bold">
                                <span>Descuento pronto pago (aplicado):</span>
                                <span>
                                  - {baseCurrency.symbol} {Number(displayProspectiveDiscount).toFixed(2)} {baseCurrency.iso}
                                  {appliedExchangeRate > 0 && ` | - Bs ${bsOf(hasCategoryTotals ? selectedCategoryData?.discount_now_bs : categoryTotals?.period?.discount_now_bs, displayProspectiveDiscount).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            )}
                            {displayProspectiveDiscount === 0 && categoryTotals.period && discountPercentByPeriod[paramPeriod.period_id] > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 fs--1 text-warning">
                                <span>Descuento pronto pago (oportunidad):</span>
                                <span>
                                  si pagas el resto (
                                  {baseCurrency.symbol} {Number(Math.max(categoryTotals.period.pending - selectedCategoryData.pending, 0)).toFixed(2)}
                                  ) antes del{" "}
                                  {categoryTotals.period.discount_due_date
                                    ? new Date(categoryTotals.period.discount_due_date).toLocaleDateString("es-VE")
                                    : "N/D"}{" "}
                                  ahorrás{" "}
                                  {baseCurrency.symbol}{" "}
                                  {Number(
                                    Math.round(
                                      categoryTotals.period.net *
                                        (discountPercentByPeriod[paramPeriod.period_id] / 100) *
                                        100
                                    ) / 100
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between align-items-center font-weight-bold">
                              <span>Total neto a pagar:</span>
                              <span className="text-primary fs-0">
                                {baseCurrency.symbol}{" "}
                                {Number(maxPayableUsd).toFixed(2)}{" "}
                                {baseCurrency.iso}
                                {appliedExchangeRate > 0 &&
                                  ` | Bs ${bsOf(selectedCategoryData?.payable_bs, maxPayableUsd).toLocaleString("es-VE", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`}
                              </span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="d-flex justify-content-between align-items-center mb-1 fs--1">
                              <span className="text-700">Subtotal de cuentas:</span>
                              <span className="font-weight-semi-bold">
                                {baseCurrency.symbol} {Number(displayGross).toFixed(2)} {baseCurrency.iso}
                                {appliedExchangeRate > 0 && ` | Bs ${bsOf(categoryTotals?.period?.gross_bs, displayGross).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                              </span>
                            </div>
                            {displayCredit > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 fs--1 text-success font-weight-bold">
                                <span>Descuento ingresos aplicados:</span>
                                <span>
                                  - {baseCurrency.symbol} {Number(displayCredit).toFixed(2)} {baseCurrency.iso}
                                  {appliedExchangeRate > 0 && ` | - Bs ${bsOf(categoryTotals?.period?.income_credit_bs, displayCredit).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            )}
                            {prospectiveDiscountTotal > 0 && (
                              <div className="d-flex justify-content-between align-items-center mb-1 fs--1 text-success font-weight-bold">
                                <span>Descuento pronto pago:</span>
                                <span>
                                  - {baseCurrency.symbol} {Number(prospectiveDiscountTotal).toFixed(2)} {baseCurrency.iso}
                                  {appliedExchangeRate > 0 && ` | - Bs ${(prospectiveDiscountTotal * appliedExchangeRate).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                </span>
                              </div>
                            )}
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between align-items-center font-weight-bold">
                              <span>Total neto a pagar:</span>
                              <span className="text-primary fs-0">
                                {baseCurrency.symbol} {Number(maxPayableUsd).toFixed(2)} {baseCurrency.iso}
                                {appliedExchangeRate > 0 && ` | Bs ${bsOf(categoryTotals?.period?.payable_bs, maxPayableUsd).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                      {selectedCuenta.length === 0 && (
                        <span
                          className={classNames(
                            "text-danger d-inline-block fs--1 font-weight-normal d-block"
                          )}
                        >
                          Debes seleccionar al menos una cuenta
                        </span>
                      )}
                      <WizardError
                        error={errors.propertyDebtsToPay?.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>

            {REWARDS_ENABLED && role === "property" && (
              <RedeemCoinsPanel
                debtUsd={Number(maxPayableUsd) || 0}
                balanceUsd={Number(balance.data) || 0}
                onRedeemed={() => setBalanceRefreshKey((key) => key + 1)}
                onUseBalance={selectBalanceAsFunds}
              />
            )}

            <Row>
              <Col sm tag={FormGroup}>
                <Label for="acronym">Origen de fondos</Label>
                <Controller
                  name="payment_by_account"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Seleccione un fondo",
                    },
                    validate: (value) => {
                      if (amount > balance.data && current === true) {
                        return "";
                      }
                      return true;
                    },
                  }}
                  render={({ onChange, name, value }) => (
                    <Input
                      type="select"
                      name="payment_by_account"
                      id="payment_by_account"
                      value={value || "Selecciona"}
                      className={`${errors.payment_by_account?.message
                        ? "border-danger"
                        : ""
                        } form-select, overflow-auto`}
                      onChange={(event) => {
                        setFund(event.target.value === "2");
                        setCurrent(event.target.value === "1");
                        setCash(event.target.value === "3");
                        setValue("payment_by_account", event.target.value);
                        onChange(`${event.target.value}`);
                      }}
                    /*    onChange={handleInputChange}
                               innerRef={register({
                                 required: { value: true, message: "Ingresar el monto" },
                               })} */
                    >
                      <option value="Selecciona" disabled>
                        ---
                      </option>
                      <option value="1">Caja</option>
                      <option value="2">Transferencia</option>
                      <option value="3">Efectivo</option>
                    </Input>
                  )}
                />
                <WizardError
                  error={errors.payment_by_account?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </Col>
            </Row>

            {amount > balance.data && current === true && (
              <Row className="p-card">
                <Col>
                  <Alert color="danger" className="mb-0">
                    No tienes saldo suficiente en la Caja para realizar el pago,
                    puedes realizar un abono ahora mismo pulsando el siguiente
                    enlace
                    <Button
                      data-testid="Button"
                      tag={Link}
                      color="link"
                      size="sm"
                      className="ml-1 pl-0"
                      to={`/dashboard/my/finance/advance/add`}
                    >
                      Abonar saldo
                    </Button>
                  </Alert>
                </Col>
              </Row>
            )}
            {fund === true && (
              <Row>
                <Col tag={FormGroup}>
                  <Label>Seleccione un banco del condominio</Label>
                  <Row>
                    <Col tag={FormGroup} xs={9} className="pr-0">
                      <Input
                        type="text"
                        placeholder="Ingrese el nombre de un banco"
                        onChange={(event) => setSearch(event.target.value)}
                      />
                    </Col>
                    <Col tag={FormGroup} xs={3}>
                      <ButtonIcon
                        className="btn-block"
                        style={{ whiteSpace: "nowrap" }}
                        icon={faSearch}
                        color="falcon-default"
                        onClick={() =>
                          setParams((prev) => {
                            return { ...prev, search };
                          })
                        }
                      >
                        Buscar
                      </ButtonIcon>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {!cbIsLoading ? (
                        condominiumBanks.length >= 1 ? (
                          renderBanksItems()
                        ) : (
                          <p>No se han encontrado bancos.</p>
                        )
                      ) : (
                        <Loader padding="py-3" />
                      )}
                      <FullPagination
                        nextPage={cbNextPage}
                        prevPage={cbPreviousPage}
                        goToPage={cbGoToPage}
                        meta={cbMeta}
                        className={"shadow-none"}
                        posText={"bancos"}
                        perPage={cbPerPage}
                      />
                      {!selectedBankId && (
                        <span
                          className={classNames(
                            "text-danger d-inline-block fs--1 font-weight-normal d-block"
                          )}
                        >
                          Debes seleccionar un banco
                        </span>
                      )}
                      <WizardError
                        error={errors.bank_id?.message}
                        className="fs--1 font-weight-normal d-block"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
            {fund === true && (
              <FormGroup>
                <Label for="name">Nombre del banco</Label>
                <Input
                  disabled
                  defaultValue={bank?.name || undefined}
                  autoComplete="off"
                  name="name"
                  id="title"
                  placeholder="Nombre del banco"
                />
              </FormGroup>
            )}
            {fund === true && (
              <Row form>
                <Col sm tag={FormGroup}>
                  <Label for="account_number">Número de cuenta</Label>
                  <NumberFormat
                    disabled
                    value={bank?.account_number || undefined}
                    format="#### #### #### #### #### #### ####"
                    placeholder="Número de cuenta"
                    className="form-control"
                    renderText={(value, props) => <div {...props}>{value}</div>}
                  />
                </Col>
                <Col sm tag={FormGroup}>
                  <Label for="owner_name">Nombre del propietario</Label>
                  <Input
                    disabled
                    defaultValue={bank?.owner_name || undefined}
                    autoComplete="off"
                    name="owner_name"
                    id="owner_name"
                    placeholder="Nombre del propietario"
                  />
                </Col>
              </Row>
            )}
            {fund === true && (
              <Row form>
                <Col sm tag={FormGroup}>
                  <Label for="owner_dni">Identificación (DNI)</Label>
                  <Input
                    defaultValue={bank?.owner_dni || undefined}
                    id="owner_dni"
                    name="owner_dni"
                    disabled
                    type="text"
                    placeholder="Identificación"
                  />
                </Col>
              </Row>
            )}
            {fund === true && (
              <Row>
                <Col tag={FormGroup}>
                  <FileUploadArea
                    onChange={handleImageChange}
                    preview={imagePreview}
                    fileName={file?.name}
                    onRemove={() => { setFile(null); setImagePreview(null); }}
                    error={errors.voucher?.message}
                  />
                </Col>
              </Row>
            )}
            {cash === true && (
              <Row>
                <Col tag={FormGroup}>
                  <FileUploadArea
                    onChange={handleImageChange}
                    preview={imagePreview}
                    fileName={file?.name}
                    onRemove={() => { setFile(null); setImagePreview(null); }}
                    error={errors.voucher?.message}
                  />
                </Col>
              </Row>
            )}

            {fund === true && (
              <Row>
                <Col tag={FormGroup}>
                  <Label>Código de referencia</Label>

                  <Input
                    className={classNames({
                      "border-danger": errors?.reference?.message,
                    })}
                    type="text"
                    name="reference"
                    id="reference"
                    /*          onChange={({ e }) => {
                          setValue("reference", e.target.value);
                          onChange(`${e.target.value}`);
                        }} */

                    innerRef={register({
                      required: "El número de referencia es obligatorio.",
                      minLength: {
                        value: 5,
                        message:
                          "El número de referencia debe tener al menos 5 caracteres.",
                      },
                    })}
                  /*    onChange={(e) => setValue("reference", e.target.value)
                  onChange(`${e.target.value}`)
                } */
                  />
                  <WizardError
                    error={errors?.reference?.message}
                    className="fs--1 font-weight-normal d-block"
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col tag={FormGroup}>
                <Label for="note">Nota</Label>
                <Input
                  type="textarea"
                  name="note"
                  id="note"
                  placeholder="Escribe una nota adicional..."
                  innerRef={register({
                    maxLength: {
                      value: 200,
                      message: "La nota no puede exceder los 200 caracteres.",
                    },
                  })}
                  onChange={(e) => {
                    if (e.target.value.length > 200) {
                      e.target.value = e.target.value.slice(0, 200);
                    }
                  }}
                />
                <WizardError
                  error={errors.note?.message}
                  className="fs--1 font-weight-normal d-block"
                />
              </Col>
            </Row>
          </CardBody>
        </Col>
        <Col className="pl-lg-2 mb-3" lg={4}>
          <CardBody className="bg-light">
            <FormGroup>
              <Label for="payment_date">
                Fecha del pago <span className="text-danger">*</span>
              </Label>
              <Controller
                name="payment_date"
                control={control}
                rules={{ required: "La fecha del pago es obligatoria." }}
                render={({ onChange, value: dateValue }) => (
                  <Datetime
                    value={dateValue ? moment(dateValue) : ""}
                    className={`w-100 ${errors.payment_date?.message ? "border-danger" : ""}`}
                    onChange={(date) => {
                      const parsedDate = moment(date);
                      onChange(
                        parsedDate.isValid()
                          ? parsedDate.format("YYYY-MM-DD")
                          : ""
                      );
                    }}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                  />
                )}
              />
              <WizardError
                error={errors.payment_date?.message}
                className="fs--1 font-weight-normal d-block"
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="mb-1">Monto total a pagar:</Label>
              <p className="mb-0 fs--1 font-weight-semi-bold text-800">
                {isRateLoading ? (
                  <small className="text-muted font-weight-normal">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                    Consultando tasa...
                  </small>
                ) : (
                  <NumberFormat
                    displayType="text"
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    value={displayMaxPayable}
                    prefix={
                      selectedCurrencyData?.iso
                        ? `${selectedCurrencyData.iso} `
                        : `${baseCurrency.symbol} `
                    }
                  />
                )}
              </p>
            </FormGroup>

            <p className="mb-0 font-weight-semi-bold">Monto a pagar</p>
            <Row className="align-items-center py-2">
              <Col>
                <Controller
                  name="montoOriginal"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "Ingresar el monto",
                    },
                  }}
                  render={({ onChange, value: formValue }) => (
                    <>
                      <InputWithLeftSelect
                        stacked
                        selectLabel="Moneda a pagar"
                        selectValue={watchedOriginalCurrency || ""}
                        onSelectChange={handleCurrencyChange}
                        selectOptions={(currencyCatalog || []).map((item) => ({
                          value: String(item.id),
                          label: `${item.iso} (${item.name})`,
                        }))}
                        errorMessage={errors.montoOriginal?.message}
                        helpText={helpText}
                        selectAriaLabel="Moneda original"
                      >
                        <Label className="mb-1 font-weight-semi-bold">Monto a pagar</Label>
                        <NumberFormat
                          customInput={Input}
                          thousandSeparator
                          prefix={
                            selectedCurrencyData?.iso
                              ? `${selectedCurrencyData.iso} `
                              : ""
                          }
                          fixedDecimalScale
                          decimalScale={2}
                          onValueChange={(vals) => {
                            const nextValue = vals.value || "";
                            hasManualAmountOverrideRef.current =
                              Number(nextValue) !== Number(lastAutoAmountRef.current ?? 0);
                            onChange(nextValue);
                          }}
                          value={formValue || ""}
                          placeholder="0.00"
                          className={classNames("font-weight-bold shadow-sm", {
                            "border-danger": errors.montoOriginal,
                          })}
                        />
                      </InputWithLeftSelect>
                      {isRateLoading && (
                        <small className="text-muted d-block mt-1">
                          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                          Consultando tasa para la fecha...
                        </small>
                      )}
                      <RateRuleNotice rule={rateRule} />
                      {!isSelectedCurrencyUsd && rateResolution?.isFallback && !isRateLoading && !isRateFrozen && (
                        <Alert color="warning" className="mt-2 mb-0 py-2 fs--1">
                          No se encontró la tasa oficial registrada para la fecha{" "}
                          <strong>{moment(rateResolution.selectedDateKey).format("DD/MM/YYYY")}</strong>.
                          {canRegisterRate ? (
                            <>
                              {" "}
                              <Button
                                color="link"
                                className="p-0 fs--1 font-weight-bold text-decoration-none ml-1"
                                onClick={() => setIsRateModalOpen(true)}
                              >
                                Registrar tasa
                              </Button>
                            </>
                          ) : (
                            " Se está utilizando una tasa de referencia."
                          )}
                        </Alert>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {Boolean(watchedOriginalAmount) && !isSelectedCurrencyUsd && (
              <div className="border border-warning bg-soft-warning rounded mb-2 py-2 px-3">
                <span className="fs--1 text-700">
                  El monto que se guardara en el sistema sera el equivalente en {getCurrencyName(baseCurrency.iso)} {baseCurrency.symbol}. El valor mostrado es la referencia original ingresada por el usuario.
                </span>
              </div>
            )}

            {appliedExchangeRate && appliedExchangeRate !== 1 && isSelectedCurrencyUsd && (
              <Row className="mt-1">
                <Col>
                  <span className="fs--1 text-700">
                    Equivalente:{" "}
                    <NumberFormat
                      className="text-nowrap"
                      value={formatAmountRounded(convertFromUsd(value, false, appliedExchangeRate), 2)}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix={`${curr?.currency?.iso} `}
                      displayType="text"
                    />
                  </span>
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                {totals.approved > 0 && (
                  <span className="fs--1 text-success mb-2 d-block">
                    Monto pagado:{" "}
                    <NumberFormat
                      className="text-nowrap"
                      value={data?.length > 0 ? totals.approved : 0}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix={`${baseCurrency.symbol} `}
                      displayType="text"
                    />
                  </span>
                )}
                {totals.pending > 0 && (
                  <span className="fs--1 text-warning mb-2 d-block">
                    Monto por aprobar:{" "}
                    <NumberFormat
                      className="text-nowrap"
                      value={data?.length > 0 ? totals.pending : 0}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix={`${baseCurrency.symbol} `}
                      displayType="text"
                    />
                  </span>
                )}
                {paramPeriod.period_id && incomeCreditTotal > 0 && (
                  <span className="fs--1 text-info mb-2 d-block">
                    Descuento aplicado (ingreso a planilla):{" "}
                    <NumberFormat
                      className="text-nowrap"
                      value={incomeCreditTotal}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix={`${baseCurrency.symbol} `}
                      displayType="text"
                    />
                  </span>
                )}
                {paramPeriod.period_id && prospectiveDiscountTotal > 0 && (
                  <span className="fs--1 text-success mb-2 d-block">
                    Descuento por pronto pago aplicado al total a pagar:{" "}
                    <NumberFormat
                      className="text-nowrap"
                      value={prospectiveDiscountTotal}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix={`${baseCurrency.symbol} `}
                      displayType="text"
                    />
                  </span>
                )}
              </Col>
            </Row>
            <ButtonIcon
              color="primary"
              icon={faDollarSign}
              onClick={() => collapseOneOpen(!collapseOne)}
              className="btn-block"
              disabled={value <= 0 || isRateLoading}
            >
              Emitir pago
            </ButtonIcon>
          </CardBody>
        </Col>
      </Row>
      <Modal isOpen={collapseOne} data-testid="modal">
        <ModalHeader>Confirmación</ModalHeader>
        <ModalBody>
          <p className="mb-0 font-weight-bold fs--1">
            ¿Está seguro de que ingresó todos los datos correctamente? Esta
            información no puede ser cambiada posteriormente.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button disabled={isPending} onClick={() => collapseOneOpen(!collapseOne)}>
            Cancelar
          </Button>
          <Button
            data-testid="submit-button"
            color="primary"
            type="submit"
            disabled={isPending || isRateLoading}
            onClick={handleSubmit(save, (formErrors) => {
              // Ver comentario equivalente en PropertyToPays.jsx: el modal de
              // confirmación tapa el formulario, así que un bloqueo por campo
              // obligatorio sin completar antes no se veía.
              const firstError = Object.values(formErrors)[0];
              Toast.fire({
                type: "error",
                title:
                  firstError?.message ||
                  "Revisa los campos obligatorios antes de continuar (banco, referencia, etc.).",
              });
            })}
          >
            {isPending && <Spinner size="sm" className="mr-2" />}
            Sí, pagar
          </Button>
        </ModalFooter>
      </Modal>
      <CreateExhangeRateModal
        isOpen={isRateModalOpen}
        setIsOpen={setIsRateModalOpen}
        currency={selectedCurrencyData || { name: "", iso: "", id: null }}
        baseCurrency={baseCurrency}
        mutate={bumpRateRefreshTick}
        condominiumId={effectiveCondominiumId}
        suggestedValue={activeExchangeRate}
      />
    </Fragment>
  );
};

export default withRedirect(PropertyToPaysDetails);
